import React from "react";
import ButtonNew from "common/Button/Button";
import { useTranslation } from "react-i18next";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { useParams, useLocation, Link as RouterLink } from "react-router-dom";
import { isBusinessStationPath } from "Helpers/stationRoutes";
import { Breadcrumbs, Link, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { resolvePageBreadcrumbs } from "Helpers/resolvePageBreadcrumbs";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";

interface DynamicDiv {
  heading: string;
  value: string;
}

interface ComponentHeaderTwoProps {
  showBtn?: boolean;
  onClick?: () => void;
  onSubmit?: () => void;
  heading?: string;
  subHeading1?: string;
  subHeading2?: string;
  icon?: React.ReactNode;
  mainImg?: React.ReactNode;
  dynamicDivs?: DynamicDiv[];
  showDetails?: boolean;
  btnText?: string;
  btnType?: string;
  showSaveBtn?: boolean;
  isBtnEnabled?: boolean;
  showCreateBtn?: boolean;
  saveBtnText?: string;
  /** When set with showCreateBtn, renders an Add/Create button in addition to the primary btn (e.g. Back). */
  onCreateClick?: () => void;
  createBtnText?: string;
  /** Overrides the last breadcrumb label (e.g. "Edit"). */
  lastBreadcrumbLabel?: string;
  /** Fully overrides resolved breadcrumbs when provided. */
  breadcrumbItems?: BreadcrumbItem[];
}

const ComponentHeaderTwo: React.FC<ComponentHeaderTwoProps> = ({
  showBtn = false,
  onClick = () => {},
  onSubmit = () => {},
  heading,
  icon,
  mainImg,
  subHeading1,
  subHeading2,
  dynamicDivs = [],
  showDetails,
  btnText,
  btnType,
  showSaveBtn,
  isBtnEnabled,
  showCreateBtn,
  saveBtnText,
  onCreateClick,
  createBtnText,
  lastBreadcrumbLabel,
  breadcrumbItems,
}) => {
  const params = useParams();
  const { pathname } = useLocation();
  const isUpdateMode =
    !!params?.stationId || (!!params?.id && !isBusinessStationPath(pathname));
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar" || i18n.language === "ur";
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const crumbs = breadcrumbItems?.length
    ? breadcrumbItems
    : resolvePageBreadcrumbs(pathname);

  return (
    <div
      className={`flex justify-between items-center rounded-lg py-1 pr-3 text-white top-[68px] z-20 mb-4`}
      style={{
        minHeight: showDetails ? "100px" : "62px",
        background: themeColors?.primary,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
        <span style={{ marginRight: "20px" }}>{mainImg}</span>
        <div
          style={{
            display: showDetails ? "" : "flex",
            alignItems: "center",
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              gap: 4,
            }}
          >
            <span
              className="invert"
              style={{ marginRight: "2px", flexShrink: 0 }}
            >
              {icon}
            </span>
            {crumbs?.length ? (
              <Breadcrumbs
                separator={
                  <NavigateNextIcon
                    fontSize="small"
                    sx={{
                      color: "rgba(255,255,255,0.55)",
                      fontSize: 15,
                      transform: isRtl ? "scaleX(-1)" : undefined,
                    }}
                  />
                }
                aria-label="breadcrumb"
                sx={{
                  "& .MuiBreadcrumbs-ol": {
                    flexWrap: "wrap",
                    alignItems: "center",
                  },
                  "& .MuiBreadcrumbs-separator": {
                    mx: 0.5,
                  },
                }}
              >
                {crumbs.map((crumb, index) => {
                  const isLast = index === crumbs.length - 1;
                  const label =
                    isLast && lastBreadcrumbLabel
                      ? lastBreadcrumbLabel
                      : crumb.label || t(crumb.labelKey);

                  if (!isLast && crumb.href) {
                    return (
                      <Link
                        key={`${crumb.labelKey}-${index}`}
                        component={RouterLink}
                        to={crumb.href}
                        underline="hover"
                        className="font-Saira"
                        sx={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: "rgba(255,255,255,0.78)",
                          lineHeight: 1.3,
                          "&:hover": { color: "#FFFFFF" },
                        }}
                      >
                        {label}
                      </Link>
                    );
                  }

                  return (
                    <Typography
                      key={`${crumb.labelKey}-${index}`}
                      className="font-Saira"
                      sx={{
                        fontSize: 15,
                        fontWeight: isLast ? 700 : 600,
                        color: isLast ? "#FFFFFF" : "rgba(255,255,255,0.78)",
                        lineHeight: 1.3,
                        maxWidth: isLast ? 280 : undefined,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </Typography>
                  );
                })}
              </Breadcrumbs>
            ) : (
              <h1 className="mt-1 font-Saira font-bold text-lg ml-1 line-clamp-1">
                {heading && t(heading)}
              </h1>
            )}
          </div>
          <h1 className="float-left font-Saira font-bold text-sm mt-1	">
            {subHeading1 && t(subHeading1)}
          </h1>
          <br />
          <h1 className="float-left font-Saira font-semibold text-sm italic	">
            {subHeading2 && t(subHeading2)}
          </h1>
        </div>
      </div>

      <div style={{ display: "flex" }}>
        {dynamicDivs.map((div, index) => (
          <div
            key={index}
            className="border-r border-white border-solid p-[6px]"
          >
            <h1 className="text-white font-Saira font-[500] text-sm">
              {div.heading}
            </h1>
            <h1 className="text-white font-Saira font-bold text-[15px]">
              {div.value}
            </h1>
          </div>
        ))}
      </div>
      <div className="flex pl-3">
        {showBtn && (
          <div style={{ marginLeft: "10px", display: "flex", gap: 8 }}>
            {showCreateBtn && onCreateClick ? (
              <ButtonNew
                onClick={onCreateClick}
                type="create"
                disabled={false}
                text={createBtnText || "add"}
                hasIcon
              ></ButtonNew>
            ) : null}
            {showCreateBtn && btnType === "create" && !onCreateClick ? (
              <ButtonNew
                onClick={onClick}
                type={btnType}
                disabled={false}
                text={btnText}
                hasIcon
              ></ButtonNew>
            ) : !showCreateBtn && btnType === "create" ? null : (
              <ButtonNew
                onClick={onClick}
                type={btnType}
                disabled={false}
                text={btnText}
                hasIcon
              ></ButtonNew>
            )}
          </div>
        )}
        {showSaveBtn && (
          <div style={{ marginLeft: "10px" }}>
            <ButtonNew
              onClick={onSubmit}
              type={!isBtnEnabled ? "disabled" : "create"}
              disabled={!isBtnEnabled}
              text={saveBtnText || (isUpdateMode ? "update" : "submit")}
              hasIcon
            ></ButtonNew>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComponentHeaderTwo;
